import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { PhotoUpload, EvidenceThumb } from "../components/Evidence";
import { toast } from "sonner";
import { CheckCircle, XCircle, Warning, ShieldWarning, Prohibit, Truck, Package, Scales, Plus, X } from "@phosphor-icons/react";
import { fmtDate } from "../lib/date";
import AssetPicker from "../components/AssetPicker";

const METER_UNIT_KEYS = new Set(["miles", "mi", "km", "kilometres", "kms", "hours", "hrs", "h", "engine_hours", "operating_hours", "cycles"]);
function meterFor(asset) {
  if (!asset) return null;
  const mu = asset.measurement_unit;
  if (mu === "engine_hours" || mu === "operating_hours" || mu === "hours") return { type: "hours", label: "Engine / operating hours", unit: "h", noun: "machine" };
  if (mu === "cycles") return { type: "cycles", label: "Cycles", unit: "cyc", noun: "item" };
  if (mu === "km") return { type: "km", label: "Odometer (km)", unit: "km", noun: "vehicle" };
  if (mu === "miles" || asset.is_road_vehicle) return { type: "miles", label: "Odometer (miles)", unit: "mi", noun: "vehicle" };
  return null;
}

const HIGH_JUMP = { miles: 2000, km: 3000, hours: 200, cycles: 2000 };
function lastReadingFor(asset, meter) {
  if (!asset || !meter) return null;
  if (meter.type === "miles" || meter.type === "km") return asset.current_mileage ?? null;
  if (meter.type === "hours") return asset.current_hours ?? null;
  if (meter.type === "cycles") return asset.current_cycles ?? null;
  return null;
}

const DECISIONS = [["safe_to_continue", "Safe to continue", "border-yellow-500 bg-yellow-50"], ["restricted_use", "Restricted use", "border-amber-500 bg-amber-50"], ["stop_using", "Stop using", "border-red-500 bg-red-50"]];

// Details for a fault raised by a failed question. Critical questions are locked to Stop using (server-enforced too).
function CheckFaultDetail({ q, value, openFaults, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const opts = q.is_critical ? DECISIONS.filter(([k]) => k === "stop_using") : DECISIONS;
  return (
    <div data-testid={`check-fault-${q.id}`} className="mt-3 border-t border-slate-200 pt-3 flex flex-col gap-2">
      <span className="text-xs font-bold uppercase text-slate-500">Can the asset still be used?</span>
      <div className="grid grid-cols-3 gap-2">
        {opts.map(([k, l, cls]) => (
          <button key={k} type="button" data-testid={`check-fault-decision-${q.id}-${k}`} onClick={() => set("use_decision", k)}
            className={`h-10 rounded border-2 text-xs font-semibold ${(value.use_decision || (q.is_critical ? "stop_using" : "")) === k ? cls : "border-slate-200"}`}>{l}</button>
        ))}
      </div>
      <Textarea data-testid={`check-fault-notes-${q.id}`} value={value.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Describe the problem (optional)" className="min-h-[50px]" />
      <div className="flex items-center gap-2 flex-wrap">
        <PhotoUpload category="check" label="Add photo" onUploaded={(f) => set("photos", [...(value.photos || []), f.id])} />
        {(value.photos || []).map((p) => <EvidenceThumb key={p} fileId={p} />)}
      </div>
      {openFaults.length > 0 && (
        <select data-testid={`check-fault-link-${q.id}`} value={value.link_fault_id || ""} onChange={(e) => set("link_fault_id", e.target.value || undefined)} className="h-10 border-2 border-slate-300 rounded px-2 text-sm">
          <option value="">New fault (not already reported)</option>
          {openFaults.map((f) => <option key={f.id} value={f.id}>Same as open fault: {f.title || f.description}</option>)}
        </select>
      )}
    </div>
  );
}

function AnswerControl({ q, value, onChange }) {
  const type = q.answer_type;
  if (type === "photo") {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <PhotoUpload category="check" label="Take / Upload Photo" onUploaded={(f) => onChange(f.id)} />
        {value && <EvidenceThumb fileId={value} />}
      </div>
    );
  }
  if (type === "good_attention_fail") {
    const opts = [["good", "Good", "bg-emerald-600 text-white border-emerald-700"],
                  ["attention", "Attention", "bg-amber-400 text-slate-900 border-amber-600"],
                  ["fail", "Fail", "bg-red-600 text-white border-red-700"]];
    return (
      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        {opts.map(([o, lbl, cls]) => (
          <button key={o} type="button" data-testid={`answer-${q.id}-${o}`} onClick={() => onChange(o)}
            className={`h-14 rounded font-bold uppercase text-sm border-2 transition-colors duration-100 ${value === o ? cls : "bg-white border-slate-300 text-slate-700"}`}>{lbl}</button>
        ))}
      </div>
    );
  }
  if (type === "signature") {
    return <Input data-testid={`answer-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder="Type your full name to sign" className="h-12 border-2 font-signature" />;
  }
  if (["yes_no", "pass_fail", "pass_fail_na", "confirmation"].includes(type)) {
    const opts = type === "yes_no" ? ["yes", "no"] : type === "confirmation" ? ["confirmed"] : (type === "pass_fail_na" ? ["pass", "fail", "na"] : ["pass", "fail"]);
    return (
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${opts.length},1fr)` }}>
        {opts.map((o) => {
          const bad = ["no", "fail"].includes(o);
          const active = value === o;
          return (
            <button key={o} type="button" data-testid={`answer-${q.id}-${o}`} onClick={() => onChange(o)}
              className={`h-14 rounded font-bold uppercase text-sm border-2 transition-colors duration-100 ${
                active ? (bad ? "bg-red-600 text-white border-red-700" : "bg-emerald-600 text-white border-emerald-700")
                       : "bg-white border-slate-300 text-slate-700"}`}>
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  if (type === "number" || type === "measurement") {
    return <Input data-testid={`answer-${q.id}`} type="number" value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={q.unit ? `Value (${q.unit})` : "Value"} className="h-12 border-2" />;
  }
  if (type === "date") return <Input data-testid={`answer-${q.id}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-12 border-2" />;
  if (type === "multiple_choice") {
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options || []).map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)} data-testid={`answer-${q.id}-${o}`}
            className={`px-3 h-11 rounded border-2 font-semibold ${value === o ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}>{o}</button>
        ))}
      </div>
    );
  }
  return <Textarea data-testid={`answer-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="border-2" />;
}

export default function CheckRunnerPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [choices, setChoices] = useState([]);
  const [gate, setGate] = useState(null);       // check-action gate for current user
  const [askAck, setAskAck] = useState(null);   // { nil } pending acknowledgement
  const [submitting, setSubmitting] = useState(false);
  const [asset, setAsset] = useState(null);
  const [meterValue, setMeterValue] = useState("");
  const [openFaults, setOpenFaults] = useState([]);
  const [loadout, setLoadout] = useState(null);
  const [loadoutConfirm, setLoadoutConfirm] = useState({});
  const [towing, setTowing] = useState({ isTowing: null, trailer: null, loads: [], advisory: null });
  const [picker, setPicker] = useState(null);  // 'trailer' | 'load'
  const [ctxToken, setCtxToken] = useState(null);   // signed frozen check-context token from /begin
  const [ctxTowbar, setCtxTowbar] = useState(false); // authoritative towbar flag frozen at check start
  const [faultDisp, setFaultDisp] = useState({});  // fault_id -> { disposition, note, worsened }
  const [faultReports, setFaultReports] = useState({});  // question_id -> { use_decision, notes, photos, link_fault_id }
  const [submissionId] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  const setDisp = (fid, disposition) => setFaultDisp((p) => ({ ...p, [fid]: { ...(p[fid] || {}), disposition, ...(disposition === "appears_rectified" ? { worsened: false } : {}) } }));
  const setDispField = (fid, k, v) => setFaultDisp((p) => ({ ...p, [fid]: { ...(p[fid] || {}), [k]: v } }));
  const clearDisp = (fid) => setFaultDisp((p) => { const n = { ...p }; delete n[fid]; return n; });

  const meter = meterFor(asset);
  const templateHasMeter = !!template?.questions?.some((q) => (q.answer_type === "number" || q.answer_type === "measurement") && METER_UNIT_KEYS.has((q.unit || "").toLowerCase()));
  const needMeter = !!meter && !templateHasMeter;
  const lastReading = lastReadingFor(asset, meter);
  const meterNum = meterValue === "" ? null : Number(meterValue);
  const tooLow = needMeter && meterNum != null && lastReading != null && meterNum < lastReading;
  const tooHigh = needMeter && meterNum != null && lastReading != null && (meterNum - lastReading) > (HIGH_JUMP[meter?.type] ?? Infinity);

  // Begin the check: freeze the asset-derived conditions server-side and get a signed token +
  // the authoritative question set. Guarantees displayed == submitted == snapshotted questions.
  const startCheck = async (tmpl) => {
    setTemplate(tmpl);
    if (!id) return;
    try {
      const b = (await api.get(`/checks/begin?asset_id=${id}&template_id=${tmpl.id}`)).data;
      setCtxToken(b.context_token);
      setCtxTowbar(!!b.towbar_fitted);
      setTemplate({ ...tmpl, questions: b.questions });
    } catch { setCtxToken(null); }
  };

  useEffect(() => {
    const tid = params.get("template");
    const resolve = async () => {
      if (id) {
        api.get(`/eligibility/asset/${id}/my-actions`).then((r) => setGate(r.data.actions.check)).catch(() => {});
        api.get(`/assets/${id}`).then((r) => setAsset(r.data)).catch(() => {});
      }
      if (tid) {
        const r = await api.get(`/checks/templates/${tid}`);
        await startCheck(r.data); setLoading(false); return;
      }
      if (id) {
        try {
          const list = (await api.get(`/checks/applicable/${id}`)).data;
          if (list.length === 0) { setMsg("No checklist is assigned for this asset. Ask an administrator to assign one on the Checks page."); setLoading(false); return; }
          if (list.length === 1) { const r = await api.get(`/checks/templates/${list[0].id}`); await startCheck(r.data); setLoading(false); return; }
          setChoices(list); setLoading(false); return;
        } catch (e) { setMsg("Could not load the checklist for this asset."); setLoading(false); return; }
      }
      setLoading(false);
    };
    resolve();
  }, [id, params]);

  const pick = async (tid) => { const r = await api.get(`/checks/templates/${tid}`); await startCheck(r.data); setChoices([]); };

  useEffect(() => {
    if (!id) return;
    api.get(`/faults?asset_id=${id}`).then((r) => setOpenFaults((r.data || []).filter((f) => !f.final_signoff_at))).catch(() => {});
  }, [id]);

  // SP4/SP5 — pull this vehicle's loadout requirements once we know it's a road vehicle.
  useEffect(() => {
    if (!id || !asset?.is_road_vehicle) { setLoadout(null); return; }
    api.get(`/equipment/vehicle/${id}/loadout`).then((r) => {
      setLoadout(r.data);
      const init = {};
      (r.data.requirements || []).forEach((rq) => { init[rq.id] = rq.status === "present" ? "present" : "missing"; });
      setLoadoutConfirm(init);
    }).catch(() => setLoadout(null));
  }, [id, asset]);

  const runAdvisory = async (trailer, loads) => {
    if (!trailer) { setTowing((t) => ({ ...t, advisory: null })); return; }
    try {
      const r = await api.post(`/equipment/towing/advisory`, { vehicle_id: id, trailer_id: trailer.id, load_asset_ids: loads.map((l) => l.id) });
      setTowing((t) => ({ ...t, advisory: r.data }));
    } catch { /* advisory only — never blocks */ }
  };
  const onPickAsset = async (a) => {
    if (picker === "trailer") { const next = { ...towing, trailer: a }; setTowing(next); runAdvisory(a, next.loads); }
    else if (picker === "load") {
      if (!towing.loads.some((l) => l.id === a.id)) {
        const loads = [...towing.loads, a]; setTowing((t) => ({ ...t, loads })); runAdvisory(towing.trailer, loads);
        // Keep the Asset Tree and the journey in sync where practical.
        if (towing.trailer && window.confirm(`Also move ${a.asset_number} — ${a.name} into ${towing.trailer.asset_number} so records match what's physically on the trailer?`)) {
          try { await api.post(`/assets/${a.id}/move`, { container_asset_id: towing.trailer.id }); toast.success("Moved into trailer"); runAdvisory(towing.trailer, towing.loads); }
          catch (e) { toast.error(e.response?.data?.detail || "Could not move"); }
        }
      }
    }
    setPicker(null);
  };
  const removeLoad = (lid) => { const loads = towing.loads.filter((l) => l.id !== lid); setTowing((t) => ({ ...t, loads })); runAdvisory(towing.trailer, loads); };
  const loadWt = (l) => l.weight_kg || l.transport_weight_kg || l.operating_weight_kg || null;
  const advLoadWt = (l) => towing.advisory?.loads?.find((x) => x.asset_id === l.id)?.weight_kg ?? loadWt(l);

  // Applicable question set = base + towbar(when frozen flag set) + towing_trailer(when towing now).
  const applicableQuestions = () => (template?.questions || []).filter((q) => {
    const aw = (q.applies_when || "").trim();
    if (aw === "towbar") return ctxTowbar;
    if (aw === "towing_trailer") return towing.isTowing === true;
    return true;
  });

  const doSubmit = async (nil, acknowledgement) => {
    if (!ctxToken) { toast.error("This check needs to be reopened before it can be completed."); return; }
    setSubmitting(true);
    const applic = applicableQuestions();
    const applicIds = new Set(applic.map((q) => q.id));
    const responses = {};
    applic.forEach((q) => { if (answers[q.id] !== undefined) responses[q.id] = { value: answers[q.id] }; });
    const meter_reading = needMeter && meterValue !== "" ? { meter_type: meter.type, value: Number(meterValue) } : undefined;
    const fault_updates = Object.entries(faultDisp).filter(([, d]) => d.disposition)
      .map(([fault_id, d]) => ({ fault_id, disposition: d.disposition, note: d.note || null, worsened: !!d.worsened }));
    const loadout_summary = loadout && loadout.requirements?.length ? {
      vehicle_label: loadout.vehicle_label,
      items: loadout.requirements.map((rq) => ({
        label: rq.expected_label || rq.label, required: rq.required, detected: rq.status,
        confirmed: loadoutConfirm[rq.id] || (rq.status === "present" ? "present" : "missing"),
      })),
    } : undefined;
    const towing_summary = towing.isTowing === true ? {
      towing: true,
      trailer: towing.trailer ? { id: towing.trailer.id, label: `${towing.trailer.asset_number} — ${towing.trailer.name}` } : null,
      loads: towing.loads.map((l) => ({ id: l.id, label: `${l.asset_number} — ${l.name}` })),
      advisory: towing.advisory ? { verdict: towing.advisory.verdict, label: towing.advisory.label, reasons: towing.advisory.reasons } : null,
    } : (towing.isTowing === false ? { towing: false } : undefined);
    try {
      const r = await api.post("/checks/complete", { asset_id: id, template_id: template.id, check_context_token: ctxToken,
        responses, nil_defect: nil, acknowledgement, meter_reading, fault_updates, loadout: loadout_summary, towing: towing_summary,
        fault_reports: faultReports, client_submission_id: submissionId });
      const raised = (r.data.faults_raised || []).length;
      // Record the departure/loadout combination (independent of whether a check was strictly needed).
      if (towing.isTowing === true && towing.trailer) {
        api.post("/equipment/loadout/events", {
          vehicle_asset_id: id, trailer_asset_id: towing.trailer.id, completed_check_id: r.data.id,
          advisory: towing.advisory || null,
          items: towing.loads.map((l) => ({ asset_id: l.id, label: `${l.asset_number} — ${l.name}` })),
        }).catch(() => {});
      }
      if (r.data.outcome === "fail") toast.error("Critical defect reported — asset set to DO NOT USE");
      else if (raised > 0) toast.warning(`${raised} defect${raised > 1 ? "s" : ""} reported`);
      else toast.success("✓ Nil defects reported");
      nav(`/assets/${id}`);
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); setAskAck(null); }
  };

  const submit = (nil = false) => {
    if (needMeter && meterValue === "") { toast.error(`Enter the current ${meter.label.toLowerCase()} before submitting`); return; }
    if (tooLow) { toast.error(`Reading can't be lower than the last recorded ${lastReading} ${meter.unit}. An Admin/Supervisor must record a correction.`); return; }
    if (towing.isTowing === true && !towing.trailer) { toast.error("Select the trailer you are towing, or choose No."); return; }
    if (gate && gate.gate === "block") { toast.error("You are not permitted to complete this check for this item."); return; }
    if (gate && gate.gate === "override") { toast.error("A manager must authorise this before you can complete the check."); return; }
    if (gate && gate.gate === "acknowledge") { setAskAck({ nil }); return; }
    doSubmit(nil, false);
  };

  if (loading) return <p className="text-slate-500">Loading check…</p>;
  if (!template && choices.length > 0) return (
    <div className="flex flex-col gap-3 max-w-lg">
      <h1 className="font-display text-2xl font-black text-slate-900">Choose a checklist</h1>
      <p className="font-body text-sm text-slate-600">This asset needs more than one check. Pick which to complete now.</p>
      {choices.map((c) => (
        <button key={c.id} data-testid={`choose-check-${c.id}`} onClick={() => pick(c.id)}
          className="text-left bg-white border-2 border-slate-300 rounded-lg p-4 hover:border-blue-500 transition-colors duration-100 font-body font-semibold text-slate-800">
          {c.name} <span className="font-mono text-xs text-slate-500">v{c.version}</span>
        </button>
      ))}
    </div>
  );
  if (!template) return <p className="text-slate-600">{msg}</p>;

  const blocked = gate && (gate.gate === "block" || gate.gate === "override");

  return (
    <div className="flex flex-col gap-4 max-w-2xl pb-40 md:pb-4">
      {asset?.effective_status && asset.effective_status.dot !== "green" && (
        <div data-testid="check-use-status-banner"
          className={`w-full border-y-4 px-4 py-3 flex flex-col items-center text-center ${asset.effective_status.dot === "red" ? "bg-red-600 border-red-800 text-white" : "bg-amber-400 border-amber-600 text-slate-900"}`}>
          <div className="flex items-center gap-2">
            {asset.effective_status.dot === "red" ? <Prohibit size={28} weight="fill" /> : <Warning size={24} weight="fill" />}
            <span className="font-display text-xl sm:text-2xl font-black uppercase tracking-wide">
              {asset.effective_status.dot === "red" ? "Do Not Use" : "Restricted Use"}
            </span>
          </div>
          {asset.effective_status.reasons?.length > 0 && (
            <p className="mt-1 font-body text-sm font-semibold">{asset.effective_status.reasons.join(" · ")}</p>
          )}
          <p className="mt-1 font-body text-xs opacity-90">Recording this check does not authorise use or change the Use Status above.</p>
        </div>
      )}
      <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">{template.name}
        <span className="ml-2 font-mono text-sm text-slate-500">v{template.version}</span></h1>
      {template.description && <p className="font-body text-slate-600 text-sm">{template.description}</p>}

      {gate && gate.gate !== "allow" && (
        <div data-testid="check-eligibility-banner"
          className={`rounded-lg border-2 p-3 flex gap-3 ${blocked ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"}`}>
          {blocked ? <ShieldWarning size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                   : <Warning size={22} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />}
          <div>
            <p className="font-body text-sm font-semibold text-slate-800">{gate.message}</p>
            {gate.missing?.length > 0 && (
              <ul className="mt-1 list-disc pl-5 font-body text-xs text-slate-600">
                {gate.missing.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            )}
            {gate.gate === "override" && <p className="mt-1 font-body text-xs font-bold text-red-700">A supervisor must add an authorisation before this check can be completed.</p>}
            {gate.gate === "block" && <p className="mt-1 font-body text-xs font-bold text-red-700">This check is blocked for you.</p>}
          </div>
        </div>
      )}

      {needMeter && (
        <div data-testid="check-meter-prompt" className={`bg-blue-50 border-2 rounded-lg p-4 ${meterValue === "" ? "border-blue-400" : tooLow ? "border-red-400" : tooHigh ? "border-amber-400" : "border-emerald-400"}`}>
          <p className="font-body font-bold text-slate-800">{meter.label} <span className="ml-1 text-xs font-bold uppercase text-red-600">Required</span></p>
          <p className="font-body text-xs text-slate-500 mb-2">Recorded against this {meter.noun} and used to schedule its next service — enter it before completing the check.</p>
          <Input data-testid="check-meter-value" type="number" inputMode="numeric" value={meterValue} onChange={(e) => setMeterValue(e.target.value)} placeholder={`Current reading (${meter.unit})`} className="h-12 border-2 text-lg font-mono" />
          {lastReading != null && <p className="font-body text-xs text-slate-500 mt-2">Last recorded: <span className="font-mono font-bold text-slate-700">{lastReading} {meter.unit}</span></p>}
          {tooLow && (
            <div data-testid="check-meter-toolow" className="mt-2 flex gap-2 rounded bg-red-50 border-2 border-red-300 p-2">
              <ShieldWarning size={18} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
              <p className="font-body text-xs font-semibold text-red-700">This reading is <b>lower</b> than the last recorded {lastReading} {meter.unit}. A reading can't go backwards — re-check the {meter.noun} and enter the correct value. If the previous reading was wrong, an Admin/Supervisor must record a correction on the asset.</p>
            </div>
          )}
          {tooHigh && !tooLow && (
            <div data-testid="check-meter-toohigh" className="mt-2 flex gap-2 rounded bg-amber-50 border-2 border-amber-300 p-2">
              <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
              <p className="font-body text-xs font-semibold text-amber-700">That's {Math.round(meterNum - lastReading).toLocaleString()} {meter.unit} more than the last reading ({lastReading} {meter.unit}) — higher than expected since the last check. Please double-check before submitting.</p>
            </div>
          )}
        </div>
      )}

      {applicableQuestions().map((q) => (
        <div key={q.id} data-testid={`question-${q.id}`}
          className={`bg-white border-2 rounded-lg p-4 ${q.is_critical ? "border-red-300" : "border-slate-200"}`}>
          <div className="flex items-start justify-between gap-2 mb-3">
            <p className="font-body font-semibold text-slate-800">{q.prompt}
              {q.is_critical && <span className="ml-2 text-xs font-bold uppercase text-red-600">Critical</span>}</p>
          </div>
          {q.guidance && <p className="font-body text-xs text-slate-500 mb-2">{q.guidance}</p>}
          <AnswerControl q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
          {(q.is_critical || q.creates_fault) && ["fail", "no", "false", "defect"].includes(String(answers[q.id]).toLowerCase()) && (
            <CheckFaultDetail q={q} value={faultReports[q.id] || {}} openFaults={openFaults}
              onChange={(v) => setFaultReports((r) => ({ ...r, [q.id]: v }))} />
          )}
        </div>
      ))}

      {loadout && loadout.requirements?.length > 0 && (
        <div data-testid="check-loadout" className="bg-white border-2 border-blue-200 rounded-lg p-4 flex flex-col gap-3">
          <div className="font-display text-lg font-black text-slate-900 flex items-center gap-2"><Package size={20} weight="bold" />Vehicle loadout</div>
          <p className="font-body text-xs text-slate-500">Quick-check the required kit is on board. This is recorded with your check — it never changes the vehicle's Use Status.</p>
          {loadout.requirements.map((rq) => {
            const val = loadoutConfirm[rq.id] || (rq.status === "present" ? "present" : "missing");
            return (
              <div key={rq.id} data-testid={`loadout-req-${rq.id}`} className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                <div className="min-w-0">
                  <div className="font-body font-semibold text-sm text-slate-800">{rq.expected_label || rq.label}{!rq.required && <span className="ml-1 text-xs text-slate-400">(optional)</span>}</div>
                  {rq.found && <div className="font-mono text-xs text-emerald-600 truncate">Detected on board: {rq.found.label}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button type="button" data-testid={`loadout-present-${rq.id}`} onClick={() => setLoadoutConfirm((p) => ({ ...p, [rq.id]: "present" }))}
                    className={`h-9 px-3 rounded text-xs font-bold uppercase border-2 transition-colors duration-100 ${val === "present" ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-slate-300 text-slate-600"}`}>On board</button>
                  <button type="button" data-testid={`loadout-missing-${rq.id}`} onClick={() => setLoadoutConfirm((p) => ({ ...p, [rq.id]: "missing" }))}
                    className={`h-9 px-3 rounded text-xs font-bold uppercase border-2 transition-colors duration-100 ${val === "missing" ? "bg-amber-500 border-amber-700 text-slate-900" : "bg-white border-slate-300 text-slate-600"}`}>Missing</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {asset?.towbar_fitted && (
        <div data-testid="check-towing" className="bg-white border-2 border-slate-200 rounded-lg p-4 flex flex-col gap-3">
          <div className="font-display text-lg font-black text-slate-900 flex items-center gap-2"><Truck size={20} weight="bold" />Are you towing today?</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="towing-yes" onClick={() => setTowing((t) => ({ ...t, isTowing: true }))}
              className={`h-12 rounded font-bold uppercase text-sm border-2 transition-colors duration-100 ${towing.isTowing === true ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"}`}>Yes</button>
            <button type="button" data-testid="towing-no" onClick={() => setTowing({ isTowing: false, trailer: null, loads: [], advisory: null })}
              className={`h-12 rounded font-bold uppercase text-sm border-2 transition-colors duration-100 ${towing.isTowing === false ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"}`}>No</button>
          </div>
          {towing.isTowing === true && (
            <div className="flex flex-col gap-3">
              <div>
                <div className="font-body text-sm font-semibold text-slate-700 mb-1">Trailer</div>
                {towing.trailer ? (
                  <div className="flex items-center justify-between border-2 border-slate-200 rounded-lg px-3 py-2" data-testid="towing-trailer">
                    <div className="min-w-0"><div className="font-body font-semibold text-sm truncate">{towing.trailer.name}</div><div className="font-mono text-xs text-slate-500">{towing.trailer.asset_number}</div></div>
                    <Button size="sm" variant="outline" data-testid="towing-change-trailer" onClick={() => setPicker("trailer")}>Change</Button>
                  </div>
                ) : (
                  <Button data-testid="towing-pick-trailer" onClick={() => setPicker("trailer")} className="h-12 w-full bg-blue-600 hover:bg-blue-700 font-bold uppercase">Scan / Select trailer</Button>
                )}
              </div>
              {towing.trailer && (
                <div>
                  <div className="font-body text-sm font-semibold text-slate-700 mb-1">Load items (optional)</div>
                  <p className="text-xs text-slate-500 mb-2">Items already inside the trailer/vehicle are included automatically below — you only add something here if it is being carried for this journey but isn't yet recorded as contained.</p>
                  {towing.loads.map((l) => (
                    <div key={l.id} data-testid={`towing-load-${l.asset_number}`} className="flex items-center justify-between border-2 border-slate-200 rounded-lg px-3 py-2 mb-1">
                      <div className="min-w-0"><div className="font-body font-semibold text-sm truncate">{l.name}</div><div className="font-mono text-xs text-slate-500">{l.asset_number}{advLoadWt(l) ? ` · ${advLoadWt(l).toLocaleString()} kg` : ""}</div></div>
                      <button type="button" data-testid={`towing-remove-${l.asset_number}`} onClick={() => removeLoad(l.id)} className="text-slate-400 hover:text-red-600 shrink-0"><X size={18} weight="bold" /></button>
                    </div>
                  ))}
                  <Button variant="outline" data-testid="towing-add-load" onClick={() => setPicker("load")} className="h-11 w-full border-2 font-semibold"><Plus size={16} weight="bold" className="mr-1" />Add load item</Button>
                </div>
              )}
              {towing.advisory && (
                <div data-testid="towing-advisory" className={`rounded-lg border-2 p-3 ${towing.advisory.verdict === "overweight" ? "bg-red-50 border-red-300" : towing.advisory.verdict === "getting_close" ? "bg-amber-50 border-amber-300" : towing.advisory.verdict === "unknown" ? "bg-slate-50 border-slate-300" : "bg-emerald-50 border-emerald-300"}`}>
                  <div className="flex items-center gap-2 font-display font-black text-slate-900"><Scales size={20} weight="bold" />{towing.advisory.label}</div>
                  <div className="mt-2 font-body text-sm text-slate-700 space-y-2">
                    {towing.advisory.trailer_contents?.length > 0 && (
                      <div data-testid="adv-trailer-contents">
                        <div className="font-semibold text-slate-700">Trailer contents — automatically included</div>
                        {towing.advisory.trailer_contents.map((c) => (
                          <div key={c.asset_id} className="flex justify-between text-xs text-slate-600"><span>{c.label}</span><span>{c.weight_kg != null ? `${c.weight_kg.toLocaleString()} kg` : "weight not recorded"}</span></div>
                        ))}
                      </div>
                    )}
                    {towing.advisory.vehicle_contents?.length > 0 && (
                      <div data-testid="adv-vehicle-contents">
                        <div className="font-semibold text-slate-700">Vehicle contents — automatically included</div>
                        {towing.advisory.vehicle_contents.map((c) => (
                          <div key={c.asset_id} className="flex justify-between text-xs text-slate-600"><span>{c.label}</span><span>{c.weight_kg != null ? `${c.weight_kg.toLocaleString()} kg` : "weight not recorded"}</span></div>
                        ))}
                      </div>
                    )}
                    {towing.advisory.vehicle_side_kg != null && (
                      <div>Estimated loaded vehicle weight (vehicle + contents): <b>{Math.round(towing.advisory.vehicle_side_kg).toLocaleString()} kg</b></div>
                    )}
                    {towing.advisory.towed_kg != null && (
                      <div>Estimated trailer gross / towed weight (trailer empty + contents): <b>{Math.round(towing.advisory.towed_kg).toLocaleString()} kg</b></div>
                    )}
                    {towing.advisory.train_kg != null && (
                      <div>Estimated train weight (loaded vehicle + trailer gross): <b>{Math.round(towing.advisory.train_kg).toLocaleString()} kg</b></div>
                    )}
                    {towing.advisory.checks?.map((c) => (
                      <div key={c.key} data-testid={`adv-check-${c.key}`} className="flex justify-between gap-2 border-t border-black/5 pt-1">
                        <span>{c.label}: <b>{c.limit_kg.toLocaleString()} kg</b></span>
                        <span className={c.exceeded ? "text-red-600 font-bold" : "text-slate-700"}>
                          {c.remaining_kg >= 0 ? `${Math.round(c.remaining_kg).toLocaleString()} kg remaining` : `${Math.round(-c.remaining_kg).toLocaleString()} kg over`}
                        </span>
                      </div>
                    ))}
                    {towing.advisory.limiting && (
                      <div className="font-semibold pt-1" data-testid="adv-limiting">Limiting factor: {towing.advisory.limiting.label} — {Math.round(towing.advisory.limiting.remaining_kg).toLocaleString()} kg estimated remaining.</div>
                    )}
                    {towing.advisory.unavailable?.map((u, i) => <div key={i} className="text-xs text-slate-500">{u}</div>)}
                    {towing.advisory.checks?.length === 0 && (!towing.advisory.unavailable || towing.advisory.unavailable.length === 0) && (
                      <div>{towing.advisory.reasons?.[0] || "Not enough weight data recorded to calculate."}</div>
                    )}
                  </div>
                  <p className="mt-2 font-body text-[11px] text-slate-500 uppercase font-bold">Estimated guide only — an asset's own weight is the item itself; contained equipment is added on top. Occupants, fuel and consumables also affect real weight. Not a weighbridge measurement, and never blocks the check.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {openFaults.length > 0 && (
        <div data-testid="fault-reconcile" className="bg-white border-2 border-amber-300 rounded-lg p-4 flex flex-col gap-3">
          <div className="font-display text-lg font-black text-slate-900 flex items-center gap-2"><Warning size={20} weight="bold" />Existing recorded faults</div>
          <p className="font-body text-xs text-slate-500">These are already open for this asset. Recording an observation here never closes them and never changes the Use Status.</p>
          {openFaults.map((f) => {
            const d = faultDisp[f.id] || {};
            return (
              <div key={f.id} data-testid={`reconcile-fault-${f.id}`} className="border-2 border-slate-200 rounded p-2">
                <div className="font-body font-semibold text-sm text-slate-900">{f.title || f.description}</div>
                <div className="font-mono text-xs text-slate-500">Reported {fmtDate(f.reported_at)}{f.status_name ? ` · ${f.status_name}` : ""}</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button type="button" data-testid={`reconcile-still-${f.id}`} onClick={() => setDisp(f.id, "still_present")}
                    className={`h-8 px-3 rounded text-xs font-bold uppercase border-2 ${d.disposition === "still_present" ? "bg-amber-500 border-amber-700 text-slate-900" : "border-slate-300 text-slate-600"}`}>Still present</button>
                  <button type="button" data-testid={`reconcile-rectified-${f.id}`} onClick={() => setDisp(f.id, "appears_rectified")}
                    className={`h-8 px-3 rounded text-xs font-bold uppercase border-2 ${d.disposition === "appears_rectified" ? "bg-blue-600 border-blue-800 text-white" : "border-slate-300 text-slate-600"}`}>Appears rectified</button>
                  {d.disposition && <button type="button" data-testid={`reconcile-clear-${f.id}`} onClick={() => clearDisp(f.id)} className="h-8 px-2 text-xs font-semibold text-slate-500">Clear</button>}
                </div>
                {d.disposition === "still_present" && (
                  <label className="flex items-center gap-2 text-xs mt-2 text-slate-700"><input type="checkbox" data-testid={`reconcile-worse-${f.id}`} checked={!!d.worsened} onChange={(e) => setDispField(f.id, "worsened", e.target.checked)} />Defect has worsened</label>
                )}
                {d.disposition && <Input data-testid={`reconcile-note-${f.id}`} placeholder="Note (optional)" value={d.note || ""} onChange={(e) => setDispField(f.id, "note", e.target.value)} className="h-8 mt-2" />}
                {d.disposition === "appears_rectified" && <p className="font-body text-xs text-amber-700 mt-1">Recorded as reported rectified and set to Awaiting Verification — an authorised person must verify/close it. The asset's Use Status is unchanged.</p>}
              </div>
            );
          })}
        </div>
      )}

      <div className="fixed bottom-16 inset-x-0 md:static bg-white md:bg-transparent border-t-2 md:border-0 border-slate-300 p-3 md:p-0 flex gap-3 md:mt-4 z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] md:shadow-none">
        <Button data-testid="submit-nil-defect" disabled={blocked || submitting || tooLow} onClick={() => submit(true)}
          className="flex-1 h-14 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase disabled:opacity-50"><CheckCircle size={20} weight="bold" className="mr-1" />Nil Defects</Button>
        <Button data-testid="submit-check" disabled={blocked || submitting || tooLow} onClick={() => submit(false)}
          className="flex-1 h-14 bg-slate-900 hover:bg-slate-800 font-bold uppercase disabled:opacity-50">Submit Check</Button>
      </div>

      {picker && (
        <AssetPicker open={!!picker} excludeId={id}
          title={picker === "trailer" ? "Scan or select trailer" : "Scan or select load item"}
          description={picker === "trailer" ? "Tap the trailer's NFC tag, scan its QR, or pick from the list." : "Add an item you're loading onto the trailer."}
          onPick={onPickAsset} onClose={() => setPicker(null)} />
      )}

      {askAck && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="ack-dialog">
          <div className="bg-white rounded-xl max-w-md w-full p-5 flex flex-col gap-3 border-2 border-amber-300">
            <div className="flex items-center gap-2">
              <Warning size={24} weight="fill" className="text-amber-600" />
              <h2 className="font-display text-lg font-black text-slate-900">Confirm you understand</h2>
            </div>
            <p className="font-body text-sm text-slate-700">{gate?.message}</p>
            {gate?.missing?.length > 0 && (
              <ul className="list-disc pl-5 font-body text-xs text-slate-600">
                {gate.missing.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            )}
            <p className="font-body text-xs text-slate-500">This acknowledgement is recorded against you, this item and the check.</p>
            <div className="flex gap-2 mt-1">
              <Button data-testid="ack-cancel" variant="outline" className="flex-1 h-11 font-semibold" onClick={() => setAskAck(null)}>Cancel</Button>
              <Button data-testid="ack-proceed" disabled={submitting} className="flex-1 h-11 bg-amber-600 hover:bg-amber-700 font-bold" onClick={() => doSubmit(askAck.nil, true)}>Acknowledge &amp; Proceed</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
